import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

/**
 * DARK BAND B — "The RockLadder Approach" interstitial.
 * Precedes the "How we help" section.
 * Scroll-linked timeline hairline draws left→right.
 */
export default function RockLadderApproachBand() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const lineWidth = useTransform(scrollYProgress, [0.15, 0.85], ["0%", "100%"]);
  const labelOpacity = useTransform(scrollYProgress, [0.15, 0.3], [0.4, 1]);

  return (
    <section
      ref={ref}
      data-testid="approach-band"
      className="rl-dark-band py-28 md:py-36"
      aria-label="The RockLadder approach"
    >
      <span className="rl-dark-band__grid" aria-hidden />
      <span
        className="rl-dark-band__blob"
        aria-hidden
        style={{ width: 680, height: 680, top: -180, right: -160, background: "#F5B841" }}
      />
      <span
        className="rl-dark-band__blob"
        aria-hidden
        style={{ width: 520, height: 520, bottom: -140, left: -120, background: "#5B2A86", animationDelay: "10s" }}
      />

      <div className="rl-dark-band__inner mx-auto max-w-[1280px] px-6 md:px-10 grid md:grid-cols-12 gap-6 md:gap-10">
        <div className="md:col-span-12">
          <motion.div
            style={{ opacity: labelOpacity }}
            className="font-editorial-mono text-[10.5px] text-[#F5B841] mb-8"
          >
            THE ROCKLADDER APPROACH — 04
          </motion.div>

          <h2
            className="font-editorial-display text-[#F5F5F7] leading-[0.96] tracking-[-0.028em] max-w-[1100px]"
            style={{ fontSize: "clamp(44px, 6.6vw, 92px)", letterSpacing: "0.005em" }}
          >
            We <span className="font-editorial-italic text-[#F5B841]">consult</span>,{" "}
            <span className="font-editorial-italic text-[#F5B841]">implement</span>,{" "}
            <span className="font-editorial-italic text-[#F5B841]">manage</span>, and{" "}
            <span className="font-editorial-italic text-[#F5B841]">test</span> — across your
            entire security landscape.
          </h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 max-w-[720px] text-[16px] md:text-[18px] leading-[1.6] text-[#C7C9D2] font-light"
            style={{ letterSpacing: "0.01em" }}
          >
            An engagement that starts as a linear plan and evolves into a continuous cycle of
            resilience — inspired by NIST CSF 2.0 and DORA. Anticipate, withstand, recover, adapt.
          </motion.p>

          {/* Scroll-driven timeline hairline */}
          <div className="mt-16 h-px w-full bg-white/10 relative overflow-hidden">
            <motion.span
              style={{ width: lineWidth }}
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#5B2A86] via-[#F5B841] to-[#F5B841]"
              aria-hidden
            />
          </div>
          <div className="mt-4 flex justify-between font-editorial-mono text-[10px] text-white/50">
            <span>ANTICIPATE</span>
            <span>WITHSTAND</span>
            <span>RECOVER</span>
            <span>ADAPT</span>
          </div>
        </div>
      </div>
    </section>
  );
}
